import { motion } from "framer-motion";
import { ArrowUpRight, Users, Code2, Bot, Paintbrush } from "lucide-react";

const IMG_RECRUITMENT =
  "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?auto=format&fit=crop&w=1200&q=80";
const IMG_SOFTWARE =
  "https://static.prod-images.emergentagent.com/jobs/a2c28dda-b6c6-423c-b58a-acb578417488/images/430b223c0afe64ff34a09aebda4d35bd94aab320239b0b818840fc65f4e3d5f8.png";
const IMG_AI =
  "https://static.prod-images.emergentagent.com/jobs/a2c28dda-b6c6-423c-b58a-acb578417488/images/483edd391424998a1cc0026396c26dea9e1761d6268d7fa266291c4efa35d823.png";
const IMG_WEB =
  "https://static.prod-images.emergentagent.com/jobs/a2c28dda-b6c6-423c-b58a-acb578417488/images/b596a66c44259d3ec526822b4a55f05926298a836aa1e3098d3b709cc821ef81.png";

const services = [
  {
    id: "recruitment",
    num: "01",
    icon: Users,
    title: "Recruitment",
    kicker: "Hire smart. Hire fast.",
    copy: "We partner with founders and CTOs to source, evaluate and place senior engineers, designers, AI researchers and operators — across APAC, EU and US timezones.",
    bullets: [
      "Executive search & leadership hiring",
      "Tech hiring sprints (2-week closures)",
      "RPO, C2C and contract staffing",
      "Rigorous 4-stage assessment loop",
    ],
    chips: ["Senior Eng", "AI / ML", "Product", "Design", "Data"],
    metric: { value: "48h", label: "Avg. time to first shortlist" },
    image: IMG_RECRUITMENT,
  },
  {
    id: "software",
    num: "02",
    icon: Code2,
    title: "Software Development",
    kicker: "From zero-to-one and beyond.",
    copy: "Full-stack product engineering shipped on modern cloud — custom web apps, ERP/CRM, SaaS products, API platforms and legacy modernization.",
    bullets: [
      "Web apps, portals & dashboards",
      "Enterprise ERP · CRM · SaaS",
      "API & platform engineering",
      "Legacy modernization & migrations",
    ],
    chips: ["React", "Next.js", "FastAPI", "Node", "Go", "MongoDB", "Postgres"],
    metric: { value: "120+", label: "Products shipped" },
    image: IMG_SOFTWARE,
  },
  {
    id: "ai",
    num: "03",
    icon: Bot,
    title: "AI Chatbot Development",
    kicker: "Assistants that actually work.",
    copy: "LLM-powered chatbots and copilots with RAG, tool use and analytics. Deploy on web, WhatsApp, Slack or inside your product.",
    bullets: [
      "RAG on your internal docs",
      "Voice & multi-modal assistants",
      "Measurable KPI tracking",
      "Safety, evals & guardrails",
    ],
    chips: ["GPT-4o", "Claude", "Gemini", "LangChain", "Pinecone", "RAG"],
    metric: { value: "41%", label: "Avg. tier-1 ticket deflection" },
    image: IMG_AI,
  },
  {
    id: "web",
    num: "04",
    icon: Paintbrush,
    title: "Web Design & Development",
    kicker: "Sites that convert.",
    copy: "Editorial design systems, high-performance builds and CMS-backed content ops — engineered for Core Web Vitals and conversion.",
    bullets: [
      "Brand & marketing sites",
      "Landing pages & funnels",
      "Headless CMS (Sanity, Contentful)",
      "Performance & SEO budgeting",
    ],
    chips: ["Figma", "Framer", "Next.js", "Sanity", "Vercel"],
    metric: { value: "95+", label: "Avg. Lighthouse score" },
    image: IMG_WEB,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32"
    >
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
        <div className="md:col-span-7">
          <div className="font-mono-label mb-4">§ 01 — Services</div>
          <h2 className="font-display text-5xl md:text-7xl">
            Four disciplines.
            <br />
            <span className="text-[color:var(--fis-ink-soft)]">One system.</span>
          </h2>
        </div>
        <p className="md:col-span-5 text-[color:var(--fis-ink-soft)]">
          We work as an integrated unit — the team that finds your next hire is
          the same team that ships your platform, trains your chatbot and
          designs your homepage. No hand-offs. No vendor sprawl.
        </p>
      </div>

      {/* Cards — uniform 2x2 grid, each card packed with imagery + detail */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {services.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              className="fis-cell group flex flex-col"
              data-testid={`service-card-${s.id}`}
            >
              {/* Media */}
              <div className="relative aspect-[16/9] overflow-hidden bg-[color:var(--fis-bg-alt)]">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-100 group-hover:scale-105"
                />
                <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                  <span className="fis-chip !bg-white/90 backdrop-blur-sm">
                    <span className="dot" />
                    {s.num} · {s.title}
                  </span>
                  <div className="w-10 h-10 bg-white/90 backdrop-blur-sm grid place-items-center">
                    <Icon className="w-5 h-5 text-[color:var(--fis-ink)]" />
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-8 md:p-10 flex-1 flex flex-col">
                <h3 className="font-display text-3xl md:text-4xl">{s.title}</h3>
                <div className="mt-2 text-[color:var(--fis-accent)] text-sm font-medium">
                  {s.kicker}
                </div>
                <p className="mt-5 text-[color:var(--fis-ink-soft)] leading-relaxed">
                  {s.copy}
                </p>

                {/* Bullets */}
                <ul className="mt-6 space-y-2 text-sm">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="w-4 h-px bg-[color:var(--fis-ink)] mt-2.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                {/* Tech / capability chips */}
                <div className="mt-6 flex flex-wrap gap-1.5">
                  {s.chips.map((c) => (
                    <span
                      key={c}
                      data-testid={`service-chip-${s.id}-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="px-2.5 py-1 text-[0.7rem] font-mono-label border fis-hairline text-[color:var(--fis-ink-soft)]"
                    >
                      {c}
                    </span>
                  ))}
                </div>

                {/* Footer strip: metric + CTA */}
                <div className="mt-auto pt-8 border-t fis-hairline flex items-end justify-between gap-4">
                  <div>
                    <div className="font-display text-3xl md:text-4xl leading-none">
                      {s.metric.value}
                    </div>
                    <div className="font-mono-label mt-2">
                      {s.metric.label}
                    </div>
                  </div>
                  <a
                    href="#contact"
                    data-testid={`service-cta-${s.id}`}
                    className="fis-btn fis-btn-outline !py-3 !px-5 shrink-0"
                  >
                    Engage
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}
